import { T } from 'gt-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { EmailViewerV2 } from '@/components/email-viewer/email-viewer-v2';
import { inboundEmailToEmailData } from '@/components/email-viewer/utils';
import useGetInboundEmail from '@/api/core/inbound-emails/getInboundEmail';

interface Props {
  inboundEmailId: string | null;
  onClose: () => void;
}

/**
 * The full message, rendered properly, in a modal.
 *
 * Two reasons it isn't inline. Most supplier emails are HTML — tables of claim
 * lines, logos, quoted replies — and the inline version showed the plain-text
 * alternative in a narrow column, which turned a readable credit note into a wall of
 * broken wrapping. And the card is meant to stay small: a body that can push it to
 * arbitrary height defeats the point of a card you can judge at a glance.
 *
 * `EmailViewerV2` rather than the Smart/Thread viewer: this renders exactly ONE
 * message. In the labeling queue that is the whole job — a snap judgement on one
 * message, with the surrounding conversation as noise. The 849 review surface uses it for a
 * different reason: the message it opens here is one forwarded ONTO the email being
 * reconciled, which has its own sender and its own attachments and is emphatically
 * not part of the parent's thread.
 */
export function FullMessageDialog({ inboundEmailId, onClose }: Props) {
  const { data: email, isLoading } = useGetInboundEmail({
    inboundEmailId: inboundEmailId ?? '',
  });

  return (
    <Dialog
      open={inboundEmailId != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      {/* Wide on purpose: supplier 849s are mostly HTML TABLES of claim lines, and
          a narrow dialog wraps every column into an unreadable stack. Viewport-
          relative with a cap so it fills a laptop screen without stretching absurdly
          on an ultrawide.

          The `sm:` prefix is LOAD-BEARING. `DialogContent`'s own base classes end in
          `sm:max-w-lg`, and `cn` is plain `twMerge`, which only de-duplicates
          conflicts within the SAME modifier set — an unprefixed `max-w-*` from a
          caller is not a conflict with a `sm:`-prefixed one, so both survive and the
          variant wins the cascade at every width above 640px. An unprefixed cap here
          is therefore inert: this dialog rendered at 32rem, which is why the claim-line
          tables were unreadable. Two other dialogs in the repo work around the same
          thing by writing the cap twice (`max-w-5xl sm:max-w-5xl`). */}
      <DialogContent className="flex h-[88vh] w-[92vw] max-w-[1600px] flex-col gap-0 p-0 sm:max-w-[1600px]">
        <DialogHeader className="border-b px-5 py-3">
          <DialogTitle className="truncate text-base">
            {email?.subject ?? (
              <T context="labeling full message title">Message</T>
            )}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : !email ? (
          // Loading finished with nothing to show — a failed fetch. Spinning here
          // forever reads as a hung app and leaves no way to notice the retry.
          <div className="flex flex-1 items-center justify-center p-6 text-center text-sm text-muted-foreground">
            <T context="labeling full message load error">
              This message could not be loaded. Close and try again.
            </T>
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            {/* The dialog is a definite 88vh; without a height the previews fall
                back to a 450px porthole inside it, and a wide spreadsheet's
                horizontal scrollbar ends up below the fold. */}
            <EmailViewerV2
              emailData={inboundEmailToEmailData(email)}
              attachmentViewerHeight="calc(88vh - 9rem)"
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
